import { useGetSortableItemMenu } from '@app/utils/hooks/sortable/useGetSortableItemMenu';
import { isDefined } from '@app/utils/is';
import { buildTree, flattenTree, getProjection } from '@app/utils/sortable';
import {
  Announcements,
  defaultDropAnimation,
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  DropAnimation,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Sortable as SortableTypes } from '@typings';
import { clone, update } from 'ramda';
import React from 'react';
import { createPortal } from 'react-dom';

import { addToast } from '../Toasts';

import { SortableContextProvider, useSortableContext } from './context/SortableContext';
import { SortableItem } from './SortableItem';

interface Props<T extends SortableTypes.Id> {
  items: SortableTypes.Items<T>;
  isDisabled?: boolean;
  maxDepth?: number;
  handlePosition?: SortableTypes.HandlePosition;
  dragMode?: SortableTypes.DragMode;
  dataTestId?: string;
  renderItem: (id: T) => JSX.Element;
  validateNesting?: SortableTypes.ValidateNesting;
  onSortStart?: () => void;
  onSortEnd?: (items: SortableTypes.Items<T>) => void;
}

const MEASURING = {
  droppable: {
    strategy: MeasuringStrategy.Always,
  },
};

const DROP_ANIMATION_OFFSET = 5;

const DROP_ANIMATION_CONFIG: DropAnimation = {
  easing: 'ease-out',
  keyframes({ transform }) {
    return [
      { opacity: 1, transform: CSS.Transform.toString(transform.initial) },
      {
        opacity: 0,
        transform: CSS.Transform.toString({
          ...transform.final,
          x: transform.final.x + DROP_ANIMATION_OFFSET,
          y: transform.final.y + DROP_ANIMATION_OFFSET,
        }),
      },
    ];
  },
  sideEffects({ active }) {
    active.node.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: defaultDropAnimation.duration,
      easing: defaultDropAnimation.easing,
    });
  },
};

const ANNOUNCEMENTS_PLACEHOLDER: Announcements = {
  onDragCancel: () => '',
  onDragEnd: () => '',
  onDragMove: () => '',
  onDragOver: () => '',
  onDragStart: () => '',
};

const INDENTATION_WIDTH = 20;

const SortableComponent = <T extends SortableTypes.Id>({
  items,
  maxDepth = 0,
  isDisabled,
  dragMode,
  dataTestId,
  handlePosition,
  renderItem,
  onSortStart,
  validateNesting,
  onSortEnd,
}: Props<T>) => {
  const [offsetLeft, setOffsetLeft] = React.useState(0);

  const { activeAnnouncement, activeId, flattenedItems, overId, setActiveId, setOverId } = useSortableContext();

  const projected =
    isDefined(activeId) && isDefined(overId) ?
      getProjection({
        activeId,
        dragOffset: offsetLeft,
        indentationWidth: INDENTATION_WIDTH,
        items: flattenedItems,
        maxAllowedDepth: maxDepth,
        overId,
      })
    : null;

  const getSortableItemMenu = useGetSortableItemMenu({ maxDepth, onSortEnd, validateNesting });

  const canNestActiveItem = React.useMemo(
    () =>
      validateNesting && isDefined(activeId) ?
        validateNesting(activeId as T, (projected?.parentId ?? null) as T | null)
      : { isValid: true },
    [validateNesting, activeId, projected?.parentId],
  );

  const showValidationNotification = React.useCallback(() => {
    const { message } = canNestActiveItem;

    if (isDefined(message)) {
      addToast(message);
    }
  }, [canNestActiveItem]);

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const sortedIds = React.useMemo(() => flattenedItems.map(({ id }) => id), [flattenedItems]);
  const activeItem = isDefined(activeId) ? flattenedItems.find(({ id }) => id === activeId) : null;

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveId(active.id);
    setOverId(active.id);
    onSortStart?.();
    document.body.style.setProperty('cursor', 'grabbing');
  };

  const handleDragMove = ({ delta }: DragMoveEvent) => {
    setOffsetLeft(delta.x);
  };

  const handleDragOver = ({ over }: DragOverEvent) => {
    setOverId(over?.id ?? null);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    resetState();
    if (!canNestActiveItem.isValid) {
      showValidationNotification();
    }

    if (canNestActiveItem.isValid && projected && over) {
      const { depth, parentId } = projected;
      const clonedItems = clone(flattenTree(items));
      const overIndex = clonedItems.findIndex(({ id }) => id === over.id);
      const activeIndex = clonedItems.findIndex(({ id }) => id === active.id);
      const activeTreeItem = clonedItems[activeIndex];

      if (!isDefined(activeTreeItem)) {
        return;
      }

      const updatedItems = update(activeIndex, { ...activeTreeItem, depth, parentId }, clonedItems);
      const sortedItems = arrayMove(updatedItems, activeIndex, overIndex);
      const newItems = buildTree(sortedItems);

      onSortEnd?.(newItems as SortableTypes.Items<T>);
    }
  };

  const handleDragCancel = () => {
    resetState();
  };

  const resetState = () => {
    setOverId(null);
    setActiveId(null);
    setOffsetLeft(0);

    document.body.style.setProperty('cursor', '');
  };

  return (
    <DndContext
      accessibility={{ announcements: ANNOUNCEMENTS_PLACEHOLDER, screenReaderInstructions: { draggable: '' } }}
      sensors={sensors}
      measuring={MEASURING}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
        <ul data-testid={dataTestId} className="m-0 flex flex-col gap-1 p-0">
          {flattenedItems.map(({ id, depth, label }) => (
            <SortableItem
              key={id}
              id={id}
              label={label}
              depth={id === activeId && isDefined(projected) ? projected.depth : depth}
              dragMode={dragMode}
              handlePosition={handlePosition}
              indentationWidth={INDENTATION_WIDTH}
              render={renderItem}
              isDisabled={isDisabled}
              menuItems={getSortableItemMenu(id)}
            />
          ))}
          {createPortal(
            <DragOverlay dropAnimation={DROP_ANIMATION_CONFIG}>
              {isDefined(activeId) && isDefined(activeItem) && (
                <SortableItem
                  id={activeId}
                  isDragOverlay
                  depth={activeItem.depth}
                  dragMode={dragMode}
                  handlePosition={handlePosition}
                  indentationWidth={INDENTATION_WIDTH}
                  render={renderItem}
                  status={canNestActiveItem.isValid ? undefined : 'error'}
                  childCount={flattenTree([activeItem]).length - 1}
                  className="pointer-events-none"
                />
              )}
            </DragOverlay>,
            document.body,
          )}
        </ul>
      </SortableContext>
      {isDefined(activeAnnouncement) && (
        <span role="alert" className="sr-only">
          {activeAnnouncement}
        </span>
      )}
    </DndContext>
  );
};

export const Sortable = <T extends SortableTypes.Id>(props: Props<T>) => (
  <SortableContextProvider items={props.items}>
    <SortableComponent {...props} />
  </SortableContextProvider>
);
